"use client";

import { ProgressiveImage } from "@/component/ProgressiveImage";
import { VideoPlayer } from "@/component/VideoPlayer";
import { unseenVersionStore } from "@/store/unseenVersion";
import { MediaVersion } from "@/types/mediaArtifact";
import { observer } from "mobx-react-lite";
import type { RefCallback } from "react";
import { GeneratingVersionItem } from "../../shared/GeneratingIcon";
import styles from "../index.module.css";

export interface MediaVersionListProps {
  versions: MediaVersion[];
  current: MediaVersion;
  isVideoMedia: boolean;
  onSelectVersion: (item: MediaVersion) => void;
  scrollContainerRef: RefCallback<HTMLDivElement>;
  showHeadGradient?: boolean;
  showTailGradient?: boolean;
}

export const MediaVersionList = observer(function MediaVersionList(
  props: MediaVersionListProps
) {
  const {
    versions,
    current,
    isVideoMedia,
    onSelectVersion,
    scrollContainerRef,
    showHeadGradient = false,
    showTailGradient = false,
  } = props;
  const hasMultipleVersions = versions.length > 1;

  if (!hasMultipleVersions) {
    return null;
  }

  return (
    <div
      className="relative w-full rounded-lg overflow-hidden"
      ref={scrollContainerRef}
    >
      <div className="w-full flex gap-3">
        {versions.map((item, index) => {
          const isActive = item.id === current.id;

          return (
            <button
              key={`media-version-${item.id}`}
              type="button"
              onClick={() => onSelectVersion(item)}
              aria-label={`Select version ${index + 1}${
                isActive ? " (current)" : ""
              }`}
              className="relative flex-shrink-0 rounded-lg cursor-pointer"
            >
              {item.status !== "success" ? (
                <GeneratingVersionItem
                  className="h-[100px] w-[100px] rounded-lg"
                  size="small"
                  staticNoise
                  isError={item.status === "failed"}
                />
              ) : isVideoMedia && !item.previewSrc ? (
                <div className="h-[100px] w-[100px] rounded-lg overflow-hidden bg-background-card">
                  <VideoPlayer
                    src={item.src}
                    className={styles.previewVideoPlayer}
                    showControls={false}
                    showPlayButton={false}
                    disableClickPlay
                  />
                </div>
              ) : (
                <ProgressiveImage
                  src={item.previewSrc}
                  alt={`version ${index + 1}`}
                  containerClassName="h-[100px] w-[100px] rounded-lg overflow-hidden"
                  className="h-full w-full object-cover"
                  width={100}
                  height={100}
                  unoptimized
                />
              )}

              {isActive && (
                <div
                  className="absolute z-10 inset-0 w-full h-full rounded-lg border-[3px] border-primary"
                  style={{
                    background: `linear-gradient(0deg, rgba(82,81,246,0.40) 0%, rgba(82,81,246,0.40) 100%)`,
                  }}
                />
              )}
              {item.status === "success" &&
                !isActive &&
                unseenVersionStore.isUnseen(item.id) && (
                  <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-primary text-white rounded-[4px] text-[10px] font-bold z-20 shadow-sm leading-tight flex items-center justify-center">
                    New
                  </div>
                )}
            </button>
          );
        })}
      </div>

      <div
        className="absolute top-0 left-0 w-[70px] h-full transition-opacity duration-300 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, var(--color-background-body) 0%, transparent 100%)`,
          opacity: showHeadGradient ? 1 : 0,
        }}
      ></div>

      <div
        className="absolute top-0 right-0 w-[70px] h-full transition-opacity duration-300 pointer-events-none"
        style={{
          background: `linear-gradient(270deg, var(--color-background-body) 0%, transparent 100%)`,
          opacity: showTailGradient ? 1 : 0,
        }}
      ></div>
    </div>
  );
});
